/**
 * Phase 3A · Event → operator-friendly copy.
 *
 * Pure functions over structured events returned by
 * GET /api/admin/orders/{id}/activity. The backend never sends
 * presentation strings as source-of-truth; this module turns
 * `{event_type, changes, actor, metadata}` into what the operator sees.
 */

const STATUS_LABEL = {
  pending_payment: "Pending payment",
  paid: "Paid",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const FULFILLMENT_LABEL = {
  carrier: "Carrier shipping",
  local_delivery: "Local delivery",
  local_pickup: "Local pickup",
};

const EVENT_HEADLINE = {
  "order.created": () => "Order placed",
  "order.status_changed": (ev) => {
    const c = (ev.changes || []).find((x) => x.field === "status");
    if (!c) return "Status changed";
    return `Status changed`;
  },
  "order.edited": () => "Order edited",
  "order.cancelled": () => "Order cancelled",
  "payment.received": () => "Payment received",
  "payment.refunded": (ev) => {
    const amt = ev.metadata?.amount;
    const isFull = ev.metadata?.is_full;
    if (amt != null) return `Refund issued · $${Number(amt).toFixed(2)}${isFull ? " (full)" : ""}`;
    return "Refund issued";
  },
  "payment.refund_summary": () => "Historical refund information",
  "payment.failed": () => "Payment failed",
  "shipping.label_purchased": () => "Shipping label purchased",
  "shipping.label_voided": () => "Shipping label voided",
  "shipping.rate_selected": () => "Shipping rate selected",
  "shipping.rate_refreshed": () => "Shipping rates refreshed",
  "shipping.pre_transit": () => "Shipment awaiting carrier scan",
  "shipping.in_transit": () => "Package in transit",
  "shipping.delivered": () => "Carrier reports delivered",
  "shipping.exception": () => "Shipping exception",
  "tracking.added": () => "Tracking added",
  "tracking.updated": () => "Tracking updated",
  "fulfillment.mode_changed": () => "Fulfillment mode changed",
  "note.updated": (ev) => (
    ev.metadata?.note_field === "admin_notes" ? "Internal note updated" : "Buyer note updated"
  ),
  "email.sent": (ev) => {
    const t = ev.metadata?.template;
    const ok = ev.metadata?.ok;
    const label = EMAIL_TEMPLATE_LABEL[t] || (t ? t.replace(/_/g, " ") : "email");
    return ok === false ? `Email send failed · ${label}` : `Email sent · ${label}`;
  },
  "email.delivered": () => "Email delivered",
  "email.bounced": () => "Email bounced",
  "email.failed": () => "Email failed",
};

// Phase 3B — friendly template labels for email.sent
const EMAIL_TEMPLATE_LABEL = {
  order_received: "order received",
  admin_new_order: "admin new-order alert",
  select_welcome: "Select welcome",
  order_updated: "order updated",
  tracking_added: "tracking added",
  refund_issued: "refund issued",
  order_paid: "payment received",
  order_status_paid: "payment confirmed",
  order_status_shipped: "order shipped",
  order_status_delivered: "order delivered",
  order_status_cancelled: "order cancelled",
  order_status_pending_payment: "awaiting payment",
};

export function eventHeadline(ev) {
  const fn = EVENT_HEADLINE[ev.event_type];
  return fn ? fn(ev) : (ev.summary || ev.event_type);
}

/**
 * Renders old→new detail for a single event. Returns an array of
 * plain-language strings the timeline can show one-per-line.
 */
export function eventDetailLines(ev) {
  const lines = [];
  const md = ev.metadata || {};
  const changes = ev.changes || [];

  switch (ev.event_type) {
    case "order.status_changed": {
      const c = changes.find((x) => x.field === "status");
      if (c) {
        const from = STATUS_LABEL[c.old] || c.old || "unset";
        const to = STATUS_LABEL[c.new] || c.new;
        lines.push(`${from} → ${to}`);
      }
      break;
    }
    case "fulfillment.mode_changed": {
      const c = changes.find((x) => x.field === "fulfillment_mode");
      if (c) {
        const from = FULFILLMENT_LABEL[c.old] || c.old || "unset";
        const to = FULFILLMENT_LABEL[c.new] || c.new;
        lines.push(`${from} → ${to}`);
      }
      break;
    }
    case "tracking.added":
    case "tracking.updated": {
      if (md.carrier || md.number) lines.push(`${md.carrier || "Carrier"} · ${md.number || ""}`.trim());
      const prev = changes.find((x) => x.field === "tracking_number");
      if (prev && prev.old && prev.new) lines.push(`${prev.old} → ${prev.new}`);
      break;
    }
    case "payment.received": {
      if (md.amount != null) lines.push(`$${Number(md.amount).toFixed(2)}`);
      if (md.payment_method) lines.push(`via ${md.payment_method.toUpperCase()}`);
      break;
    }
    case "payment.refunded": {
      if (md.refund_id) lines.push(`Refund id: ${md.refund_id}`);
      break;
    }
    case "payment.refund_summary": {
      const total = md.total_refunded;
      const unknown = md.timestamp_unknown;
      if (total != null) lines.push(`$${Number(total).toFixed(2)} total refunded`);
      if (unknown) {
        lines.push("Date unavailable");
      } else if (md.last_refund_at) {
        lines.push(`Last recorded refund activity: ${new Date(md.last_refund_at).toLocaleDateString()}`);
      }
      lines.push("Individual refund transactions unavailable");
      break;
    }
    case "shipping.label_purchased": {
      if (md.carrier || md.service_display) {
        lines.push(`${md.carrier || ""} ${md.service_display || ""}`.trim());
      }
      if (md.tracking_number) lines.push(`Tracking ${md.tracking_number}`);
      break;
    }
    case "shipping.label_voided": {
      if (md.label_refund_id) lines.push(`Void refund id: ${md.label_refund_id}`);
      break;
    }
    case "shipping.rate_selected": {
      if (md.carrier || md.service_display) {
        lines.push(`${md.carrier || ""} ${md.service_display || ""}`.trim());
      }
      if (md.rate_cents != null) lines.push(`$${(Number(md.rate_cents) / 100).toFixed(2)}`);
      if (md.delivery_days != null) lines.push(`Est. ${md.delivery_days} day${md.delivery_days === 1 ? "" : "s"}`);
      break;
    }
    case "shipping.rate_refreshed": {
      if (md.option_count != null) lines.push(`${md.option_count} option${md.option_count === 1 ? "" : "s"} available`);
      if (md.cheapest_cents != null) lines.push(`Cheapest $${(Number(md.cheapest_cents) / 100).toFixed(2)}`);
      break;
    }
    case "email.sent": {
      if (md.recipient_hash) lines.push(`Recipient #${md.recipient_hash}`);
      if (md.template) lines.push(EMAIL_TEMPLATE_LABEL[md.template] || md.template.replace(/_/g, " "));
      break;
    }
    case "email.delivered": {
      if (md.recipient_hash) lines.push(`Recipient #${md.recipient_hash}`);
      if (md.template) lines.push(EMAIL_TEMPLATE_LABEL[md.template] || md.template.replace(/_/g, " "));
      break;
    }
    case "email.bounced": {
      if (md.bounce_type) lines.push(`${md.bounce_type} bounce`);
      if (md.recipient_hash) lines.push(`Recipient #${md.recipient_hash}`);
      break;
    }
    case "email.failed": {
      if (md.reason) lines.push(md.reason);
      if (md.recipient_hash) lines.push(`Recipient #${md.recipient_hash}`);
      break;
    }
    case "shipping.pre_transit":
    case "shipping.in_transit":
    case "shipping.delivered":
    case "shipping.exception": {
      const loc = [md.location_city, md.location_state].filter(Boolean).join(", ");
      if (md.carrier || md.tracking_number) {
        lines.push(`${md.carrier || ""}${md.carrier && md.tracking_number ? " · " : ""}${md.tracking_number || ""}`.trim());
      }
      if (md.status_details) lines.push(md.status_details);
      if (loc) lines.push(loc);
      break;
    }
    case "order.edited": {
      // Show up to 6 field diffs; anything more collapses under a "more" pill.
      const scalar = changes.filter((c) => !(c.field || "").startsWith("address."));
      const addr = changes.filter((c) => (c.field || "").startsWith("address."));
      scalar.slice(0, 6).forEach((c) => {
        const from = c.old == null ? "unset" : formatValue(c.field, c.old);
        const to = c.new == null ? "unset" : formatValue(c.field, c.new);
        lines.push(`${prettyField(c.field)}: ${from} → ${to}`);
      });
      if (addr.length > 0) {
        lines.push(`Address changed (${addr.length} field${addr.length === 1 ? "" : "s"})`);
      }
      if (md.fields_changed && changes.length === 0) {
        // Legacy edit — slugs only
        lines.push(md.fields_changed.map(prettyField).filter(Boolean).join(", "));
      }
      break;
    }
    case "order.created": {
      if (md.total != null) lines.push(`$${Number(md.total).toFixed(2)} · ${md.cart_tier || "supply"}`);
      if (md.manual_order) lines.push("Manual invoice");
      break;
    }
    case "note.updated": {
      const which = md.note_field === "admin_notes" ? "Internal" : "Buyer";
      if (md.cleared) lines.push(`${which} note cleared`);
      else if (md.prev_len != null && md.new_len != null)
        lines.push(`${which} note updated (${md.prev_len} → ${md.new_len} chars)`);
      break;
    }
    default:
      if (ev.summary) lines.push(ev.summary);
  }
  return lines;
}

function formatValue(field, v) {
  if (["shipping", "discount", "total", "subtotal", "amount"].includes(field)) {
    return `$${Number(v).toFixed(2)}`;
  }
  if (field === "status") return STATUS_LABEL[v] || v;
  if (field === "fulfillment_mode") return FULFILLMENT_LABEL[v] || v;
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

function prettyField(f) {
  const map = {
    status: "Status",
    shipping: "Shipping",
    discount: "Discount",
    discount_code: "Discount code",
    total: "Total",
    subtotal: "Subtotal",
    items: "Items",
    payment_method: "Payment method",
    tracking_number: "Tracking number",
    tracking_carrier: "Tracking carrier",
    note_to_buyer: "Buyer note",
    admin_notes: "Internal note",
    customer_email: "Customer email",
    customer_name: "Customer name",
    address: "Shipping address",
    fulfillment_mode: "Fulfillment mode",
    local_delivery: "Fulfillment mode",
    local_delivery_mode: "Fulfillment mode",
    pickup_notes: "Pickup notes",
    shipping_snapshot: "Shipping snapshot",
    edit_history: null,
    updated_at: null,
    user_id: null,
  };
  return map[f] !== undefined ? (map[f] || "") : f;
}

/**
 * Actor-type color rail. Kept as CSS class names so the timeline can render
 * without importing a color palette from outside.
 */
export function actorRailClass(actorType) {
  switch (actorType) {
    case "admin":     return "bg-oxblood";
    case "buyer":     return "bg-charcoal";
    case "stripe":
    case "shippo":
    case "resend":    return "bg-[#DDA94E]";
    case "email":     return "bg-[#B98A3A]";
    case "migration": return "bg-charcoal/25";
    case "system":
    default:          return "bg-charcoal/40";
  }
}

export function actorLabel(actor) {
  if (!actor) return "";
  const t = actor.type;
  const d = actor.display || "";
  if (t === "admin") return `${d || "Admin"} (admin)`;
  if (t === "buyer") return `${d || "Buyer"} (buyer)`;
  if (t === "stripe") return "Stripe";
  if (t === "shippo") return "Shippo";
  if (t === "resend") return "Resend";
  if (t === "migration") return "Legacy backfill";
  if (t === "system") return d || "System";
  return d;
}

/**
 * Group events by correlation_id. Preserves overall order. Returns
 * [{ correlation_id | null, events: [...] }] with singletons still in a group
 * of one so the renderer treats the shape uniformly.
 */
export function groupByCorrelation(events) {
  const out = [];
  let currentKey = Symbol("init");
  let currentGroup = null;
  for (const ev of events) {
    // legacy events with correlation_id: null each become their own group
    const key = ev.correlation_id || `__solo:${ev.event_id}`;
    if (key !== currentKey) {
      currentGroup = { correlation_id: ev.correlation_id || null, events: [] };
      out.push(currentGroup);
      currentKey = key;
    }
    currentGroup.events.push(ev);
  }
  return out;
}

export function formatOccurredAt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
